import { useTranslation } from 'react-i18next';

import deleteIcon from '@/assets/icons/deleteIcon.svg';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getFileIcon } from '@/utils/fileUtils';
import { cn } from '@/utils/utils';

export interface ProfileNarrativeFile {
  id: string;
  name: string;
}

interface ProfileNarrativeUploadsProps {
  className?: string;
  files: Array<ProfileNarrativeFile>;
  onDelete?: (fileId: string) => void;
}

export function ProfileNarrativeUploads({
  className,
  files,
  onDelete,
}: ProfileNarrativeUploadsProps) {
  const { t } = useTranslation();
  if (files?.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex w-full flex-col items-center gap-2', className)}>
      {files?.map?.((file) => {
        const fileIcon = getFileIcon(file?.name ?? '');

        return (
          <div
            className="border-border-subtle bg-back-bg flex h-[46px] w-full items-center gap-2 overflow-hidden rounded-xl border-[0.5px] px-[10px] shadow-[0px_1px_5px_0px_#71778B1A]"
            key={file?.id}
          >
            {/* File Icon */}
            <div className="bg-elevated-subtle flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[3px]">
              <img alt={t('profile.uploads.fileTypeAlt')} className="h-4 w-4" src={fileIcon} />
            </div>

            {/* File Name - Wrapper for tooltip positioning */}
            <div className="relative flex min-w-0 flex-1 items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex w-full min-w-0 cursor-pointer items-center overflow-hidden">
                      <p className="text-secondary min-w-0 truncate font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal">
                        {file?.name ?? ''}
                      </p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    align="end"
                    alignOffset={0}
                    className="max-w-[260px] text-left break-words [&>svg]:!absolute [&>svg]:!right-2 [&>svg]:!left-auto"
                    side="bottom"
                    sideOffset={8}
                  >
                    <p className="whitespace-normal">{file?.name ?? ''}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Delete Icon */}
            {onDelete && (
              <Button
                aria-label={t('profile.uploads.deleteAria')}
                className="hover:bg-back-bg2 flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-md p-0 transition-all duration-200"
                onClick={() => onDelete?.(file?.id ?? '')}
                size="icon"
                variant="ghost"
              >
                <img
                  alt={t('profile.uploads.deleteAlt')}
                  className="icon-themed h-3.5 w-3.5"
                  src={deleteIcon}
                />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
